<template>
  <div class="rules-form-wrapper">
    <v-form ref="form" class="rules-form-content">
      <div class="rules-form-body">

        <!-- Navigation Tabs -->
        <div class="px-2">
          <v-tabs v-model="activeTab" active-class="tab-active" background-color="transparent" color="blue darken-2"
            dense class="custom-tabs mb-4">
            <v-tab class="text-capitalize font-weight-medium">
              <v-icon small left>mdi-file-document-outline</v-icon> Datos Generales
            </v-tab>
            <v-tab class="text-capitalize font-weight-medium">
              <v-icon small left>mdi-clock-outline</v-icon> Reglas
            </v-tab>
            <v-tab class="text-capitalize font-weight-medium">
              <v-icon small left>mdi-alert-circle-outline</v-icon> Excepciones
            </v-tab>
          </v-tabs>
        </div>

        <v-tabs-items v-model="activeTab" class="px-2 rounded-lg bg-white">
          <!-- TAB 1: DATOS DE LA REGLA -->
          <v-tab-item class="py-1">
            <!-- BLOQUE 1: INFORMACIÓN GENERAL -->
            <v-card outlined class="rounded-lg border-subtle pa-4 mb-4 bg-white">
              <div class="d-flex align-center mb-2">
                <v-avatar color="blue lighten-5" size="36" class="mr-3">
                  <v-icon color="blue darken-2" small>mdi-file-document-outline</v-icon>
                </v-avatar>
                <div>
                  <h4 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-0">Información General</h4>
                  <p class="text-caption grey--text mb-0">Asigna un nombre descriptivo para identificar la regla dentro
                    del sistema.</p>
                </div>
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="px-1">
                <v-text-field v-model="dataRule.cd" :rules="[rulesValidation.required()]" label="Nombre de la regla"
                  placeholder="Ej. Disponibilidad Fin de Semana" outlined dense hide-details="auto"
                  class="custom-field"></v-text-field>
                <v-textarea v-model="dataRule.ds" :rules="[rulesValidation.required()]" label="Descripción"
                  placeholder="Ej. Disponibilidad Fin de Semana" outlined dense hide-details="auto"
                  class="custom-field mt-2"></v-textarea>
              </div>
            </v-card>

            <!-- BLOQUE 2: VIGENCIA DE LA REGLA (dataRule.vg) -->
            <v-card outlined class="rounded-lg border-subtle pa-4 mb-4 bg-white">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <v-avatar color="indigo lighten-5" size="36" class="mr-3">
                    <v-icon color="indigo darken-2" small>mdi-calendar</v-icon>
                  </v-avatar>
                  <div>
                    <h4 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-0">Vigencia del Período
                    </h4>
                    <p class="text-caption grey--text mb-0">Define la fecha de inicio y término en la que estará activa
                      esta regla.</p>
                  </div>
                </div>
                <v-switch v-model="hasVigencia" label="Definir vigencia" color="indigo darken-2" dense hide-details
                  class="mt-0 font-weight-medium"></v-switch>
              </div>
              <v-divider class="my-3"></v-divider>

              <!-- Estado inactivo: vg === null -->
              <div v-if="!hasVigencia" class="pa-4 rounded-lg text-center border-subtle bg-light-form">
                <v-icon color="indigo lighten-2" class="mb-1">mdi-calendar-remove-outline</v-icon>
                <p class="text-caption font-weight-medium blue-grey--text text--darken-2 mb-0">
                  Sin vigencia configurada. La regla no tiene restricción de vigencia por
                  fechas.
                </p>
              </div>

              <!-- Estado activo: vg !== null -->
              <div v-else class="px-1">
                <v-row dense class="align-center">
                  <!-- Fecha de Inicio (vg.in) -->
                  <v-col cols="12" sm="6">
                    <v-menu v-model="menuDateIn" :close-on-content-click="false" transition="scale-transition" offset-y
                      min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="dataRule.vg.in" label="Fecha de inicio" readonly outlined dense
                          v-bind="attrs" v-on="on" clearable prepend-inner-icon="mdi-calendar-start"
                          class="custom-field" hide-details="auto" placeholder="YYYY-MM-DD"></v-text-field>
                      </template>
                      <v-date-picker v-model="dataRule.vg.in" @input="menuDateIn = false" locale="es-es"
                        color="indigo darken-2"></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- Fecha de Término (vg.tm) -->
                  <v-col cols="12" sm="6">
                    <v-menu v-model="menuDateTm" :close-on-content-click="false" transition="scale-transition" offset-y
                      min-width="auto" :disabled="noEndDate">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="dataRule.vg.tm" label="Fecha de término" readonly outlined dense
                          v-bind="attrs" v-on="on" clearable prepend-inner-icon="mdi-calendar-end" class="custom-field"
                          hide-details="auto" :disabled="noEndDate" placeholder="YYYY-MM-DD"></v-text-field>
                      </template>
                      <v-date-picker v-model="dataRule.vg.tm" @input="menuDateTm = false" locale="es-es"
                        color="indigo darken-2" :min="dataRule.vg ? dataRule.vg.in : ''"></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- Opciones rápidas de vigencia -->
                  <v-col cols="12" class="mt-3 d-flex align-center justify-space-between flex-wrap gap-2">
                    <v-switch v-model="noEndDate" label="Sin fecha de término (Vigencia indefinida)"
                      color="indigo darken-2" dense hide-details class="mt-0"
                      @change="handleNoEndDateChange"></v-switch>

                    <div
                      class="text-caption font-weight-medium grey--text text--darken-2 pa-2 rounded-lg border-subtle bg-light-form">
                      <v-icon x-small color="indigo darken-2" class="mr-1">mdi-information-outline</v-icon>
                      <span v-if="dataRule.vg && (dataRule.vg.in || dataRule.vg.tm)">
                        Vigencia: <strong>{{ dataRule.vg.in || 'Desde el inicio' }}</strong>
                        <span v-if="!noEndDate && dataRule.vg.tm"> hasta <strong>{{ dataRule.vg.tm }}</strong></span>
                        <span v-else> (Indefinida)</span>
                      </span>
                      <span v-else>Sin fechas especificadas (Aplica permanentemente)</span>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-card>

            <!-- BLOQUE 3: CRITERIOS E INCLUSIONES (dataRule.in) -->
            <v-card outlined class="rounded-lg border-subtle pa-4 bg-white">
              <div class="d-flex align-center mb-2">
                <v-avatar color="blue lighten-5" size="36" class="mr-3">
                  <v-icon color="blue darken-2" small>mdi-filter-variant</v-icon>
                </v-avatar>
                <div>
                  <h4 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-0">Criterios e Inclusiones
                  </h4>
                  <p class="text-caption grey--text mb-0">Asigna categorías, etiquetas o productos específicos a los que
                    aplicará
                    esta regla.</p>
                </div>
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="px-1">
                <v-row dense class="align-center mb-3">
                  <v-col cols="12" sm="4">
                    <v-select v-model="auxFormDataIn.tp" :items="tiposFiltro" label="Tipo de filtro" dense hide-details
                      outlined class="custom-field" @change="auxFormDataIn.vl = ''"></v-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-autocomplete v-if="auxFormDataIn.tp !== 'T'" hide-details v-model="auxFormDataIn.vl"
                      :items="(auxFormDataIn.tp == 'P') ? dataItems.productos : (auxFormDataIn.tp == 'F') ? dataItems.familias : dataItems.categorias"
                      :label="auxFormDataIn.tp == 'P' ? 'Seleccionar producto' : (auxFormDataIn.tp == 'F') ? 'Seleccionar familia' : 'Seleccionar categoría'"
                      outlined dense class="custom-field" clearable></v-autocomplete>
                    <v-text-field v-else hide-details v-model="auxFormDataIn.vl" label="Escribir Tag" outlined dense
                      class="custom-field" clearable></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="2">
                    <v-btn color="blue darken-2" dark block depressed
                      class="rounded-lg text-capitalize font-weight-medium" :disabled="!auxFormDataIn.vl"
                      @click="addInclusion">
                      <v-icon small left>mdi-plus</v-icon> Añadir
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Lista de Inclusiones Agregadas -->
                <div class="mt-4">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wide">
                      Inclusiones Activas ({{ dataRule.in ? dataRule.in.length : 0 }})
                    </span>
                  </div>

                  <div v-if="!dataRule.in || dataRule.in.length === 0" class="empty-state text-center py-6 rounded-lg">
                    <v-avatar color="blue-grey lighten-5" size="40" class="mb-2">
                      <v-icon color="blue-grey lighten-2">mdi-filter-variant-remove</v-icon>
                    </v-avatar>
                    <p class="text-caption grey--text text--darken-1 mb-0">
                      Sin inclusiones configuradas aún.
                    </p>
                  </div>

                  <div v-else class="inclusions-list">
                    <v-card outlined v-for="(inc, index) in dataRule.in" :key="index"
                      class="mb-2 cron-item-card rounded-lg">
                      <v-card-text class="py-2 px-3 d-flex align-center justify-space-between">
                        <div class="d-flex align-center flex-wrap flex-grow-1 mr-2">
                          <v-chip x-small :color="getInclusionColor(inc.tp)" dark class="mr-2 font-weight-bold">
                            <v-icon x-small left>{{ getInclusionIcon(inc.tp) }}</v-icon>
                            {{ getInclusionTypeName(inc.tp) }}
                          </v-chip>
                          <span class="text-body-2 grey--text text--darken-3 font-weight-medium">
                            {{ getInclusionText(inc) }}
                          </span>
                        </div>
                        <v-btn icon x-small color="red lighten-1" @click="deleteInclusion(index)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </div>
                </div>
              </div>
            </v-card>
          </v-tab-item>

          <!-- TAB 2: HORARIOS (CRON) -->
          <v-tab-item class="py-1">
            <v-card outlined class="rounded-lg border-subtle pa-4 mb-4 bg-white">
              <div class="d-flex align-center justify-space-between mb-3">
                <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wide">
                  Configurar Expresión Cron
                </span>
                <v-btn text x-small color="blue darken-2" class="text-capitalize font-weight-bold px-2"
                  @click="visualDialog = true">
                  <v-icon x-small left color="blue darken-2">mdi-magic-staff</v-icon> Generador Visual
                </v-btn>
              </div>

              <!-- Entrada rápida / Pegar expresión Cron -->
              <div class="mb-3">
                <v-text-field v-model="rawCronInput" dense outlined label="Pegar o escribir expresión Cron directa"
                  placeholder="Ej: 0 8 * * 1-5" prepend-inner-icon="mdi-content-paste" clearable hide-details="auto"
                  class="custom-field" @input="onRawCronInput" @paste="handlePasteCron">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn x-small icon color="blue darken-2" v-bind="attrs" v-on="on" @click="pasteFromClipboard">
                          <v-icon small>mdi-clipboard-plus-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Pegar desde el portapapeles</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </div>

              <v-row dense class="mb-2">
                <v-col cols="6" sm="4">
                  <v-text-field dense outlined label="Minutos" v-model="minutes" placeholder="*" hide-details="auto"
                    class="custom-field"></v-text-field>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-text-field dense outlined label="Hora" v-model="hours" placeholder="*" hide-details="auto"
                    class="custom-field"></v-text-field>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-text-field dense outlined label="Día del mes" v-model="daymonth" placeholder="*"
                    hide-details="auto" class="custom-field"></v-text-field>
                </v-col>
                <v-col cols="6" sm="4" class="mt-2">
                  <v-text-field dense outlined label="Mes" v-model="month" placeholder="*" hide-details="auto"
                    class="custom-field"></v-text-field>
                </v-col>
                <v-col cols="6" sm="4" class="mt-2">
                  <v-text-field dense outlined label="Día de semana" v-model="dayweek" placeholder="*"
                    hide-details="auto" class="custom-field"></v-text-field>
                </v-col>
              </v-row>

              <!-- Preview Minimalista del Cron -->
              <div class="cron-preview-minimal pa-3 rounded-lg my-3">
                <div class="d-flex align-center justify-space-between flex-wrap gap-2">
                  <div class="d-flex align-center">
                    <span class="text-caption font-weight-bold grey--text text--darken-1 mr-2">CÓDIGO:</span>
                    <code class="cron-code-pill">{{ cronToAdd }}</code>
                  </div>
                  <div class="d-flex align-center">
                    <v-icon x-small color="blue darken-2" class="mr-1">mdi-subtitles-outline</v-icon>
                    <span class="text-caption font-weight-medium blue-grey--text text--darken-3">
                      {{ getParsedCron(cronToAdd) }}
                    </span>
                  </div>
                </div>
              </div>

              <v-btn color="blue darken-2" dark block depressed class="rounded-lg text-capitalize font-weight-medium"
                @click="addCronRule">
                <v-icon small left>mdi-plus-circle-outline</v-icon> Añadir Expresión a la Regla
              </v-btn>
            </v-card>

            <!-- Lista de Crons Agregados -->
            <div class="px-1">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wide">
                  Reglas de Horario Activas ({{ dataRule.rg ? dataRule.rg.length : 0 }})
                </span>
              </div>

              <div v-if="!dataRule.rg || dataRule.rg.length === 0" class="empty-state text-center py-6 rounded-lg">
                <v-avatar color="blue-grey lighten-5" size="40" class="mb-2">
                  <v-icon color="blue-grey lighten-2">mdi-clock-outline</v-icon>
                </v-avatar>
                <p class="text-caption grey--text text--darken-1 mb-0">
                  Sin expresiones cron configuradas aún.
                </p>
              </div>

              <div v-else class="crons-list">
                <v-card outlined v-for="(cron, index) in dataRule.rg" :key="index"
                  class="mb-2 cron-item-card rounded-lg">
                  <v-card-text class="py-2 px-3 d-flex align-center justify-space-between">
                    <div class="d-flex align-center flex-wrap flex-grow-1 mr-2">
                      <code class="cron-code-pill mr-3">{{ cron }}</code>
                      <span class="text-caption blue-grey--text text--darken-2 font-weight-medium">
                        {{ getParsedCron(cron) }}
                      </span>
                    </div>
                    <v-btn icon x-small color="red lighten-1" @click="deleteCronRule(index)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-tab-item>

          <!-- TAB 3: EXCEPCIONES -->
          <v-tab-item class="py-1">
            <v-card outlined class="rounded-lg border-subtle pa-5 bg-white">
              <div class="d-flex align-center mb-2">
                <v-avatar color="amber lighten-5" size="36" class="mr-3">
                  <v-icon color="amber darken-3" small>mdi-shield-alert-outline</v-icon>
                </v-avatar>
                <div>
                  <h4 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-0">Excepciones de Productos
                  </h4>
                  <p class="text-caption grey--text mb-0">Define productos excluidos del cumplimiento de esta regla.</p>
                </div>
              </div>
              <v-divider class="my-3"></v-divider>

              <div class="px-1">
                <!-- Formulario Selector para Excepciones -->
                <span
                  class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wide d-block mb-2">
                  Agregar Excepción de Producto
                </span>

                <v-row dense class="align-center mb-4">
                  <v-col cols="12" sm="9">
                    <v-autocomplete v-model="selectedExclusionProductId" :items="dataItems.productos" item-value="value"
                      item-text="text" label="Buscar y seleccionar producto a excluir" outlined dense hide-details
                      clearable class="custom-field">
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">{{ item.Nombre }}</v-list-item-title>
                          <v-list-item-subtitle class="caption grey--text">{{ item.Tipo }} — ID: {{ item.id
                          }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-btn color="amber darken-3" dark block depressed
                      class="rounded-lg text-capitalize font-weight-medium" :disabled="!selectedExclusionProductId"
                      @click="addExclusionProduct">
                      <v-icon small left>mdi-plus</v-icon> Excluir
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Lista de Productos Excluidos -->
                <div class="mt-4">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wide">
                      Productos Excluidos ({{ dataRule.ex ? dataRule.ex.length : 0 }})
                    </span>
                  </div>

                  <div v-if="!dataRule.ex || dataRule.ex.length === 0" class="empty-state text-center py-6 rounded-lg">
                    <v-avatar color="amber lighten-5" size="40" class="mb-2">
                      <v-icon color="amber darken-3">mdi-shield-check-outline</v-icon>
                    </v-avatar>
                    <p class="text-caption grey--text text--darken-1 mb-0">
                      Sin excepciones configuradas aún. Todos los productos especificados aplicarán la regla.
                    </p>
                  </div>

                  <div v-else class="exceptions-list">
                    <v-card outlined v-for="(prodId, index) in dataRule.ex" :key="index"
                      class="mb-2 cron-item-card rounded-lg">
                      <v-card-text class="py-2 px-3 d-flex align-center justify-space-between">
                        <div class="d-flex align-center flex-wrap flex-grow-1 mr-2">
                          <v-chip x-small color="amber darken-3" dark class="mr-2 font-weight-bold">
                            <v-icon x-small left>mdi-package-variant-remove</v-icon> Excepción
                          </v-chip>
                          <span class="text-body-2 grey--text text--darken-3 font-weight-medium mr-2">
                            {{ getExclusionProductText(prodId) }}
                          </span>

                        </div>
                        <v-btn icon x-small color="red lighten-1" @click="deleteExclusionProduct(index)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </div>
                </div>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </div>

      <!-- Botones Sticky al bottom -->
      <div class="form-footer-sticky px-4 py-3">
        <v-btn text color="grey darken-1" class="text-capitalize font-weight-medium mr-2"
          @click="$emit('toggle', false)">
          Cancelar
        </v-btn>
        <v-btn color="blue darken-2" dark depressed class="text-capitalize font-weight-medium rounded-lg px-5"
          @click="checkForm()">
          Guardar Regla
        </v-btn>
      </div>
    </v-form>

    <!-- ====== DIALOG: Generador Visual ====== -->
    <v-dialog v-model="visualDialog" max-width="600" scrollable class="rounded-lg">
      <v-card class="rounded-lg">
        <v-card-title class="d-flex align-center py-3 px-4 border-bottom-subtle">
          <v-avatar color="green lighten-5" size="32" class="mr-2">
            <v-icon small color="green darken-2">mdi-magic-staff</v-icon>
          </v-avatar>
          <span class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Generador Visual Cron</span>
          <v-spacer></v-spacer>
          <v-btn icon x-small @click="visualDialog = false"><v-icon small>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Plantillas -->
          <div class="mb-4">
            <span
              class="text-caption font-weight-bold grey--text text--darken-2 uppercase-label d-block mb-2">PLANTILLAS
              RÁPIDAS</span>
            <div class="d-flex flex-wrap gap-2">
              <v-chip filter outlined small color="blue-grey darken-1" class="chip-template"
                @click="applyTemplate('*', '*', '*', '*', '*')">Siempre</v-chip>
              <v-chip filter outlined small color="blue-grey darken-1" class="chip-template"
                @click="applyTemplate('*', '*', '*', '*', '1,2,3,4,5')">Lun - Vie</v-chip>
              <v-chip filter outlined small color="blue-grey darken-1" class="chip-template"
                @click="applyTemplate('*', '*', '*', '*', '0,6')">Fin de Semana</v-chip>
              <v-chip filter outlined small color="blue-grey darken-1" class="chip-template"
                @click="applyTemplate('*', '8,9,10,11,12', '*', '*', '*')">Mañanas (8-12)</v-chip>
              <v-chip filter outlined small color="blue-grey darken-1" class="chip-template"
                @click="applyTemplate('*', '13,14,15,16,17,18', '*', '*', '*')">Tardes (13-18)</v-chip>
              <v-chip filter outlined small color="blue-grey darken-1" class="chip-template"
                @click="applyTemplate('*', '19-23', '*', '*', '*')">(19hrs - 23hrs)</v-chip>
              <v-chip filter outlined small color="blue-grey darken-1" class="chip-template"
                @click="applyTemplate('*', '13-23', '*', '*', '*')">(13hrs - 23hrs)</v-chip>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <span class="text-caption font-weight-bold grey--text text--darken-2 uppercase-label d-block mb-2">DÍAS DE LA
            SEMANA</span>
          <v-chip-group v-model="selectedDaysWeek" multiple active-class="blue darken-2 white--text" class="mb-3">
            <v-chip v-for="day in daysWeekOptions" :key="day.value" :value="day.value" filter small class="chip-day">{{
              day.label }}</v-chip>
          </v-chip-group>

          <v-row dense class="mt-1">
            <v-col cols="12" sm="6">
              <span
                class="text-caption font-weight-bold grey--text text--darken-2 uppercase-label d-block mb-1">HORAS</span>
              <v-autocomplete v-model="selectedHours" :items="hoursOptions" multiple outlined dense small-chips
                clearable label="Cualquier hora" hide-details class="custom-field"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <span
                class="text-caption font-weight-bold grey--text text--darken-2 uppercase-label d-block mb-1">MINUTOS</span>
              <v-autocomplete v-model="selectedMinutes" :items="minutesOptions" multiple outlined dense small-chips
                clearable label="Cualquier minuto" hide-details class="custom-field"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" class="mt-3">
              <span class="text-caption font-weight-bold grey--text text--darken-2 uppercase-label d-block mb-1">DÍA DEL
                MES</span>
              <v-autocomplete v-model="selectedDaysMonth" :items="daysMonthOptions" multiple outlined dense small-chips
                clearable label="Todos los días" hide-details class="custom-field"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" class="mt-3">
              <span
                class="text-caption font-weight-bold grey--text text--darken-2 uppercase-label d-block mb-1">MESES</span>
              <v-autocomplete v-model="selectedMonths" :items="monthsOptions" multiple outlined dense small-chips
                clearable label="Todos los meses" hide-details class="custom-field"></v-autocomplete>
            </v-col>
          </v-row>

          <!-- Result Preview -->
          <div class="cron-preview-minimal pa-3 rounded-lg mt-4">
            <div class="d-flex align-center justify-space-between">
              <code class="cron-code-pill">{{ cronFromVisual }}</code>
              <span class="text-caption font-weight-medium blue-grey--text text--darken-3 ml-2">
                {{ getParsedCron(cronFromVisual) }}
              </span>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text small color="grey darken-1" class="text-capitalize" @click="visualDialog = false">Cancelar</v-btn>
          <v-btn color="blue darken-2" dark depressed small class="text-capitalize font-weight-medium rounded-lg"
            @click="applyCronFromVisual">
            <v-icon left small>mdi-check</v-icon> Aplicar este cron
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { V } from '@/helpers/ValidationRules';
import { parseHumanReadable } from "cron-js-parser";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "RulesAvailabilityForm",
  props: ["areas", "dataEdit"],
  data() {
    return {
      editMode: false,
      activeTab: 0,
      rulesValidation: V,
      rawCronInput: "",
      minutes: "*",
      hours: "*",
      daymonth: "*",
      month: "*",
      dayweek: "*",
      dataRule: {
        id: null,
        cd: "",
        ds: "",
        in: [],
        ex: [],
        rg: [],
        vg: null
      },
      menuDateIn: false,
      menuDateTm: false,
      noEndDate: false,
      selectedExclusionProductId: null,
      auxFormDataIn: {
        tp: "C",
        vl: "",
      },
      tiposFiltro: [
        { text: "Producto", value: "P" },
        { text: "Categoría", value: "C" },
        { text: "Tag", value: "T" },
        { text: "Familias", value: "F" },
      ],
      familias: [],
      Categories: [],
      visualDialog: false,
      selectedDaysWeek: [],
      selectedHours: [],
      selectedMinutes: [],
      selectedDaysMonth: [],
      selectedMonths: [],
      daysWeekOptions: [
        { label: 'Dom', value: '0' },
        { label: 'Lun', value: '1' },
        { label: 'Mar', value: '2' },
        { label: 'Mié', value: '3' },
        { label: 'Jue', value: '4' },
        { label: 'Vie', value: '5' },
        { label: 'Sáb', value: '6' },
      ],
      monthsOptions: [
        { text: 'Ene', value: '1' }, { text: 'Feb', value: '2' }, { text: 'Mar', value: '3' },
        { text: 'Abr', value: '4' }, { text: 'May', value: '5' }, { text: 'Jun', value: '6' },
        { text: 'Jul', value: '7' }, { text: 'Ago', value: '8' }, { text: 'Sep', value: '9' },
        { text: 'Oct', value: '10' }, { text: 'Nov', value: '11' }, { text: 'Dic', value: '12' }
      ],
    };
  },
  computed: {
    ...mapGetters("Categorias", ["getCategoriesFiltered"]),
    hasVigencia: {
      get() {
        return this.dataRule.vg !== null && this.dataRule.vg !== undefined;
      },
      set(val) {
        if (val) {
          if (!this.dataRule.vg) {
            this.$set(this.dataRule, 'vg', { in: '', tm: '' });
          }
        } else {
          this.dataRule.vg = null;
        }
      }
    },
    hoursOptions() {
      return Array.from({ length: 24 }, (_, i) => ({ text: `${i}:00`, value: `${i}` }));
    },
    minutesOptions() {
      return Array.from({ length: 60 }, (_, i) => ({ text: `${i} min`, value: `${i}` }));
    },
    daysMonthOptions() {
      return Array.from({ length: 31 }, (_, i) => ({ text: `Día ${i + 1}`, value: `${i + 1}` }));
    },
    cronToAdd() {
      return (
        this.clearAndValidate(this.minutes) + " " +
        this.clearAndValidate(this.hours) + " " +
        this.clearAndValidate(this.daymonth) + " " +
        this.clearAndValidate(this.month) + " " +
        this.clearAndValidate(this.dayweek)
      );
    },
    cronFromVisual() {
      const formatPart = (arr) => arr && arr.length > 0 ? arr.sort((a, b) => parseInt(a) - parseInt(b)).join(',') : '*';
      return (
        formatPart(this.selectedMinutes) + " " +
        formatPart(this.selectedHours) + " " +
        formatPart(this.selectedDaysMonth) + " " +
        formatPart(this.selectedMonths) + " " +
        formatPart(this.selectedDaysWeek)
      );
    },
    dataItems() {
      let productos = [];
      let categorias = [];
      let familias = [];
      let rowproductos = this.$store.getters.getProductos || [];

      rowproductos.forEach((element) => {
        if (element.Dlat == null) {
          productos.push({
            ...element,
            value: element.id,
            text: element.Nombre + " - " + element.Tipo
          });
        }
      });


      this.Categories.forEach((categories) => {
        categorias.push({
          ...categories,
          value: categories.id,
          text: categories.Nombre
        });
      });

      this.familias.forEach((familia) => {
        familias.push({
          ...familia,
          value: familia.id,
          text: familia.Nombre
        });
      });

      return { productos, categorias, familias };
    }




  },
  methods: {
    ...mapActions("Categorias", { fetchCategories: "getCategories" }),
    getParsedCron(expression) {
      return parseHumanReadable(expression, {}, "es");
    },
    clearAndValidate(param) {
      const clearValue = param.replace(/\s/g, "");
      if (clearValue === "") {
        return "*";
      }
      return clearValue;
    },
    applyTemplate(min, hr, dm, m, dw) {
      const parseToArray = (val) => val === '*' ? [] : val.split(',');
      this.selectedMinutes = parseToArray(min);
      this.selectedHours = parseToArray(hr);
      this.selectedDaysMonth = parseToArray(dm);
      this.selectedMonths = parseToArray(m);
      this.selectedDaysWeek = parseToArray(dw);
    },
    applyCronFromVisual() {
      const parts = this.cronFromVisual.split(' ');
      this.minutes = parts[0] || '*';
      this.hours = parts[1] || '*';
      this.daymonth = parts[2] || '*';
      this.month = parts[3] || '*';
      this.dayweek = parts[4] || '*';
      this.visualDialog = false;
    },
    addCronRule() {
      const cron = this.cronToAdd;
      if (!this.dataRule.rg) {
        this.$set(this.dataRule, 'rg', []);
      }
      if (!this.dataRule.rg.includes(cron)) {
        this.dataRule.rg.push(cron);
        this.resetCronForm();
        if (this.$toast) {
          this.$toast.success(" ", "Expresión Cron agregada");
        }
      } else {
        if (this.$toast) {
          this.$toast.warning(" ", "Esta expresión Cron ya fue agregada");
        }
      }
    },
    deleteCronRule(index) {
      this.dataRule.rg.splice(index, 1);
    },
    resetCronForm() {
      this.rawCronInput = "";
      this.minutes = "*";
      this.hours = "*";
      this.daymonth = "*";
      this.month = "*";
      this.dayweek = "*";
    },
    parseRawCron(val) {
      if (!val) return;
      const cleanVal = val.trim().replace(/\s+/g, " ");
      const parts = cleanVal.split(" ");
      if (parts.length >= 5) {
        this.minutes = parts[0] || "*";
        this.hours = parts[1] || "*";
        this.daymonth = parts[2] || "*";
        this.month = parts[3] || "*";
        this.dayweek = parts[4] || "*";
      }
    },
    onRawCronInput(val) {
      if (val) {
        this.parseRawCron(val);
      }
    },
    handlePasteCron(event) {
      const text = (event.clipboardData || window.clipboardData).getData("text");
      if (text) {
        this.parseRawCron(text);
      }
    },
    async pasteFromClipboard() {
      try {
        if (navigator.clipboard && navigator.clipboard.readText) {
          const text = await navigator.clipboard.readText();
          if (text) {
            this.rawCronInput = text;
            this.parseRawCron(text);
            if (this.$toast) {
              this.$toast.success(" ", "Expresión pegada del portapapeles");
            }
          }
        }
      } catch (err) {
        console.error("Error al leer el portapapeles:", err);
      }
    },
    checkForm() {
      const valido = this.$refs.form.validate();

      if (valido) {
        this.dataRule.edit = this.editMode;
        this.$emit("successData", this.dataRule);
      }
    },
    addInclusion() {
      if (!this.auxFormDataIn.vl) return;
      if (!this.dataRule.in) {
        this.$set(this.dataRule, 'in', []);
      }
      const exists = this.dataRule.in.some(
        item => item.tp === this.auxFormDataIn.tp && item.vl === this.auxFormDataIn.vl
      );
      if (!exists) {
        this.dataRule.in.push({
          tp: this.auxFormDataIn.tp,
          vl: this.auxFormDataIn.vl
        });
        this.auxFormDataIn.vl = "";
        if (this.$toast) {
          this.$toast.success(" ", "Inclusión agregada correctamente");
        }
      } else {
        if (this.$toast) {
          this.$toast.warning(" ", "Esta inclusión ya fue agregada");
        }
      }
    },
    deleteInclusion(index) {
      this.dataRule.in.splice(index, 1);
    },
    getInclusionText(item) {
      if (item.tp === 'P') {
        const prod = this.dataItems.productos.find(p => p.value === item.vl);
        return prod ? prod.text : item.vl;
      }
      if (item.tp === 'C') {
        const cat = this.dataItems.categorias.find(c => c.value === item.vl);
        return cat ? cat.text : item.vl;
      }
      return item.vl;
    },
    getInclusionTypeName(typeValue) {
      const type = this.tiposFiltro.find(t => t.value === typeValue);
      return type ? type.text : typeValue;
    },
    getInclusionColor(typeValue) {
      switch (typeValue) {
        case 'P': return 'blue darken-1';
        case 'C': return 'purple darken-1';
        case 'T': return 'teal darken-1';
        case 'F': return 'amber darken-2';
        default: return 'grey darken-1';
      }
    },
    getInclusionIcon(typeValue) {
      switch (typeValue) {
        case 'P': return 'mdi-package-variant-closed';
        case 'C': return 'mdi-shape-outline';
        case 'T': return 'mdi-tag-outline';
        case 'F': return 'mdi-account-group-outline';
        default: return 'mdi-filter-variant';
      }
    },
    addExclusionProduct() {
      if (!this.selectedExclusionProductId) return;
      if (!this.dataRule.ex) {
        this.$set(this.dataRule, 'ex', []);
      }
      if (!this.dataRule.ex.includes(this.selectedExclusionProductId)) {
        this.dataRule.ex.push(this.selectedExclusionProductId);
        this.selectedExclusionProductId = null;
        if (this.$toast) {
          this.$toast.success(" ", "Producto agregado a excepciones");
        }
      } else {
        if (this.$toast) {
          this.$toast.warning(" ", "Este producto ya se encuentra en excepciones");
        }
      }
    },
    deleteExclusionProduct(index) {
      this.dataRule.ex.splice(index, 1);
    },
    getExclusionProductText(prodId) {
      const prod = this.dataItems.productos.find(p => p.value === prodId || p.id === prodId);
      return prod ? prod.text : prodId;
    },
    async getFamilias() {
      const data =
        await this.$store.getters.getFirebaseApp.database.queryBuilder(
          "PDV/Productos/Familias"
        );

      this.familias = [];
      this.familias = this.$pgsHelper.ArrayObjectToArray(data.val());
    },
    handleNoEndDateChange(val) {
      if (val) {
        this.dataRule.vg.tm = "";
      }
    },
    async getCategories() {
      await this.fetchCategories();
      const claims = this.$route.params?.user?.tokenResult?.claims;
      this.Categories = this.getCategoriesFiltered(claims);
    },
  },
  beforeMount() {
    this.getCategories();
    this.getFamilias();
    if (this.dataEdit) {
      this.editMode = true;
      this.dataRule = { ...this.dataEdit };
      if (!this.dataRule.rg) {
        this.$set(this.dataRule, 'rg', []);
      }
      if (!this.dataRule.in) {
        this.$set(this.dataRule, 'in', []);
      }
      if (!this.dataRule.ex) {
        this.$set(this.dataRule, 'ex', []);
      }
      if (this.dataRule.vg === undefined) {
        this.$set(this.dataRule, 'vg', null);
      } else if (this.dataRule.vg && !this.dataRule.vg.tm) {
        this.noEndDate = true;
      }
    } else {
      if (!this.dataRule.in) {
        this.$set(this.dataRule, 'in', []);
      }
      if (!this.dataRule.ex) {
        this.$set(this.dataRule, 'ex', []);
      }
      if (this.dataRule.vg === undefined) {
        this.$set(this.dataRule, 'vg', null);
      }
    }
  },
};
</script>

<style scoped>
.rules-form-wrapper {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
}

.rules-form-content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.rules-form-body {
  flex-grow: 1;
}

.border-subtle {
  border: 1px solid #e2e8f0 !important;
}

.border-bottom-subtle {
  border-bottom: 1px solid #f1f5f9;
}

.uppercase-label {
  letter-spacing: 0.05em;
  font-size: 0.7rem;
}

.bg-light-form {
  background-color: #fafafa;
}

.custom-tabs ::v-deep .v-tabs-bar {
  border-bottom: 1px solid #e2e8f0;
}

.custom-tabs ::v-deep .v-tab {
  letter-spacing: 0;
  font-size: 0.85rem;
  color: #64748b !important;
}

.custom-tabs ::v-deep .v-tab--active {
  color: #1e40af !important;
  font-weight: 600 !important;
}

.custom-field ::v-deep .v-input__control {
  border-radius: 8px;
}

.cron-preview-minimal {
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
}

.cron-code-pill {
  font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
  font-size: 0.78rem;
  font-weight: 600;
  background-color: #e2e8f0;
  color: #1e293b;
  padding: 2px 8px;
  border-radius: 6px;
}

.empty-state {
  border: 1px dashed #e2e8f0;
  background-color: #f8fafc;
}

.cron-item-card {
  border: 1px solid #e2e8f0 !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.cron-item-card:hover {
  border-color: #cbd5e1 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.form-footer-sticky {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 5;
  background-color: #ffffff;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 0 -24px -24px -24px;
}
</style>
